<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    min-width="290px"
    offset-y
    :top="top"
    :bottom="bottom"
    transition="scale-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="formattedDate"
        :append-icon="appendIcon"
        :clearable="clearable"
        :disabled="loadingData || disabled"
        :label="label"
        :placeholder="placeholder"
        :error-messages="errors"
        dense
        readonly
        @click:append="menu = true"
        v-on="on"
        v-bind="attrs"
      />
    </template>
    <v-card>
      <v-card-text class="px-0 py-0">
        <v-tabs fixed-tabs v-model="activeTab">
          <v-tab key="calendar" @click="showDatePicker">
            <v-icon>mdi-calendar</v-icon>
          </v-tab>
          <v-tab key="timer" @click="showTimerPicker">
            <v-icon>mdi-clock-outline</v-icon>
          </v-tab>
          <v-tab-item key="calendar">
            <v-date-picker
              v-if="menu"
              v-model="dateModel"
              :locale="locale"
              :first-day-of-week="firstDayOfWeek"
              no-title
              @input="showTimerPicker"
            ></v-date-picker>
          </v-tab-item>
          <v-tab-item key="timer">
            <v-time-picker
              v-if="menu"
              ref="timer"
              class="v-time-picker-custom"
              v-model="time"
              :format="inputTimeFormat"
              :ampm-in-title="inputTimeFormat === 'ampm'"
              full-width
            ></v-time-picker>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="menu = false">
          {{ $t('components.modal.btn.cancel') }}
        </v-btn>
        <v-btn color="primary" text @click="$refs.menu.save(model)">
          {{ $t('components.modal.btn.validate') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
  import { mapState } from 'vuex'
  import { EventBus } from '@/event-bus'
  import { is12HourMoment } from '@/helpers/timeFormat'

  export default {
    name: 'DateTimeFilter',
    props: {
      loadingData: {
        type: Boolean,
        default: false,
      },
      value: {
        type: [String, Date],
      },
      placeholder: {
        type: String,
      },
      label: {
        type: String,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      appendIcon: {
        type: String,
        default: 'mdi-calendar',
      },
      errors: {
        type: [Array, String],
        default: '',
      },
      format: {
        type: Object,
        default: () => {
          return {
            date: {
              displayed: 'L LTS',
            },
            time: {
              displayed: 'LTS',
            },
          }
        },
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      top: {
        type: Boolean,
        default: true,
      },
      bottom: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        menu: false,
        formattedDate: undefined,
        activeTab: 0,
        firstDayOfWeek: this.$moment.localeData().firstDayOfWeek(),
        inputTimeFormat: is12HourMoment() ? 'ampm' : '24hr',
      }
    },
    watch: {
      menu: {
        handler() {
          this.$emit('validateDate')
        },
      },
      formattedDate: {
        handler() {
          if (this.formattedDate == null) {
            this.model = Array.isArray(this.model) ? [] : undefined
            this.$emit('resetDate')
          }
        },
      },
      model: {
        handler(newValue) {
          this.formatDate(newValue)
        },
      },
      locale: {
        handler() {
          this.firstDayOfWeek = this.$moment.localeData().firstDayOfWeek()
          this.inputTimeFormat = is12HourMoment() ? 'ampm' : '24hr'
        },
      },
    },
    computed: {
      ...mapState(['locale']),
      model: {
        get() {
          if (this.value instanceof Date) {
            return this.value.toISOString()
          }
          return this.value
        },
        set(value) {
          this.$emit('input', value)
        },
      },
      dateModel: {
        get() {
          if (!this.model) {
            return undefined
          }
          return this.$moment(this.model).format('YYYY-MM-DD')
        },
        set(value) {
          const existing = this.$moment(this.model ?? value)
          this.model = this.$moment(value)
            .set({
              hour: existing.hour(),
              minute: existing.minute(),
              second: existing.second(),
            })
            .toISOString()
        },
      },
      time: {
        get() {
          return this.$moment(this.model).format('HH:mm:ss')
        },
        set(value) {
          const timeParts = value.split(':')
          const hour = Number.parseInt(timeParts?.[0] ?? 0, 10)
          const minute = Number.parseInt(timeParts?.[1] ?? 0, 10)
          const second = Number.parseInt(timeParts?.[2] ?? 0, 10)

          this.model = this.$moment(this.model)
            .set({
              hour,
              minute,
              second,
            })
            .toISOString()
        },
      },
    },
    mounted() {
      this.resetFormHandler = () => {
        this.formattedDate = undefined
        this.model = undefined
      }
      EventBus.$on('resetForm', this.resetFormHandler)
      this.formatDate(this.value)
    },
    beforeDestroy() {
      EventBus.$off('resetForm', this.resetFormHandler)
    },
    methods: {
      formatDate(dates) {
        if (!dates) {
          this.formattedDate = undefined
          return
        }
        this.formattedDate = this.$moment(dates).format(this.format.date.displayed)
      },
      showTimerPicker() {
        this.activeTab = 1
      },
      showDatePicker() {
        this.activeTab = 0
      },
    },
  }
</script>
